import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
	Button,
	Text,
	RequestSyllabusButton,
	Link,
	CallUsModal,
} from "@website-builder/ui/shared/elements";
import { useMediaQuery } from "@react-hook/media-query";
import { isBusinessVerticalB2U } from "@website-builder/utilities/utils/helpers.js";
import { TABLET_M_BP } from "@website-builder/utilities/constants/style-constants/screen-sizes.js";
import { SecondaryNavWrapper, ModalContent, ImageWrapper } from "./styles";
import { handleCTAClick } from "@website-builder/utilities/utils/DesignUtils.js";

const AElement = (props) => React.createElement("a", props);
const isB2U = isBusinessVerticalB2U();
const SecondaryNavigationWR = (props) => {
	const secondaryNavEl = useRef(null);
	const satisfiesMobileWidth = useMediaQuery(`(max-width: ${TABLET_M_BP}px)`);

	const [showCallUsModal, setShowCallUsModal] = useState(false);
	const [isMobile, setIsMobile] = useState(undefined);

	useEffect(() => {
		setIsMobile(satisfiesMobileWidth);
	}, [satisfiesMobileWidth]);
	const brandLogoSrc =
		"https://res.cloudinary.com/springboard-images/image/upload/v1675594276/website-redesign/brand-logos/Springboard-Ball-Logo-Mobile.svg";
	const BUSINESS_VERTICAL = process.env.GATSBY_BUSINESS_VERTICAL;

	const {
		hashLinks,
		context,
		cta,
		navigationWithoutCTA,
		requestFormType,
		callUsBtn,
		pageSection,
		workshopId,
		showRequestSyllabusInMobile,
		showCallUsInMobile,
		requestSyllabusCTA = undefined,
	} = props;

	useEffect(() => {
		const sectionWrapper = document.getElementsByClassName(
			"SecondaryNavigationWR",
		)[0];
		sectionWrapper.parentElement.classList.add("sticky-secondary-nav-wrapper");
	}, []);

	useEffect(() => {
		if (BUSINESS_VERTICAL !== "b2u") {
			const observer = new IntersectionObserver(
				([e]) => {
					window.requestAnimationFrame(() => {
						if (window.scrollY > e.target.getBoundingClientRect().top) {
							e.target.classList.toggle("stuck", e.intersectionRatio < 1);
						}
					});
				},
				{ threshold: [1] },
			);
			observer.observe(secondaryNavEl.current);
		}
		return () => observer.disconnect();
	}, [secondaryNavEl]);

	return (
		<SecondaryNavWrapper
			className={`SecondaryNavigationWR ${isB2U ? "stuck" : null}`}
			ref={secondaryNavEl}
			isMobile={isMobile}
			showRequestSyllabusInMobile={showRequestSyllabusInMobile}
			showCallUsInMobile={showCallUsInMobile}
			isB2u={isB2U}
		>
			<div className="secondary-navigation-container">
				<div className="stickyWrapper">
					{isMobile && (
						<Link href="/">
							<img
								src={brandLogoSrc}
								alt="Brand Logo"
								width="30px"
								height="30px"
							/>
						</Link>
					)}
					{!isMobile && (
						<div className="linksWrapper">
							{hashLinks
								.filter((hl) => !!hl?.label && !!hl?.link && !!hl?.link?.value)
								.map(({ label, link: { value: hash } }, index) => (
									<a href={`#${hash}`} key={`${label}-${index}`}>
										<Text
											className="navigation-label"
											variant="paragraph_S"
											data-label={label}
										>
											{label}
										</Text>
									</a>
								))}
						</div>
					)}

					{!navigationWithoutCTA ? (
						<div className="ctaWrapper">
							{callUsBtn.hasOwnProperty("lowerText") &&
							(!isMobile || showCallUsInMobile) ? (
								<React.Fragment>
									<Button
										buttonConfiguration={isB2U ? callUsBtn : undefined}
										buttonText="Call Us"
										type="secondary"
										onClick={() => setShowCallUsModal(true)}
										aria-expanded={showCallUsModal}
										aria-controls={
											showCallUsModal
												? "secondary-navigation-call-us-modal"
												: null
										}
										aria-haspopup="dialog"
									/>
									<CallUsModal
										showModal={showCallUsModal}
										{...callUsBtn}
										onClose={() => setShowCallUsModal(false)}
										imageUrl={callUsBtn?.url}
										imageAltText={callUsBtn?.alt}
										pageSection={pageSection}
										id="secondary-navigation-call-us-modal"
									/>
								</React.Fragment>
							) : null}
							{!isMobile || showRequestSyllabusInMobile ? (
								<RequestSyllabusButton
									buttonText={`${isMobile ? "Get" : "Request"} Syllabus`}
									type="secondary"
									requestFormType={requestFormType}
									context={context}
									pageSection={pageSection}
									workshopId={workshopId}
									{...requestSyllabusCTA}
									cta={requestSyllabusCTA}
									modalId={`secondary-navigation-${workshopId}-request-syllabus-modal`}
								/>
							) : null}

							{cta ? (
								<Button
									buttonConfiguration={isB2U && cta}
									{...cta}
									buttonText={cta.text}
									onClick={() =>
										handleCTAClick(cta, {
											page_section: pageSection,
											redirect_to: cta?.link?.link,
											click_text: cta?.text,
										})
									}
								/>
							) : null}
						</div>
					) : null}
				</div>
			</div>
		</SecondaryNavWrapper>
	);
};

SecondaryNavigationWR.defaultProps = {
	hashLinks: [],

	externalDependency: {
		Link: AElement,
	},
	context: {
		fullSlug: "",
	},
	navigationWithoutCTA: false,
	requestFormType: "emailForm",
	pageSection: "",
	workshopId: "",
};

const HashLink = PropTypes.shape({
	type: PropTypes.oneOf(["hash"]).isRequired,
	value: PropTypes.string.isRequired,
});

SecondaryNavigationWR.propTypes = {
	hashLinks: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			link: HashLink,
		}),
	),
	externalDependency: PropTypes.shape({
		Link: PropTypes.func,
	}),
	context: PropTypes.shape({
		fullSlug: PropTypes.string,
	}),
	navigationWithoutCTA: PropTypes.bool,
	requestFormType: PropTypes.oneOf(["emailForm", "contactForm", "none"]),
	pageSection: PropTypes.string,
	workshopId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SecondaryNavigationWR;
